import {
  Component,
  effect,
  inject,
  Injector,
  input,
  signal,
  untracked,
} from '@angular/core';

import * as Highcharts from 'highcharts';
import HC_accessibility from 'highcharts/modules/accessibility';
import HC_exporting from 'highcharts/modules/exporting';
HC_exporting(Highcharts);
HC_accessibility(Highcharts);
import { ThemeService } from '../shared/services/theme.service';
import { StationInfo } from './smw.service';

import { TranslateService } from '@ngx-translate/core';

import { HighchartsChartModule } from 'highcharts-angular';
import moment from 'moment';
import { isNil } from 'src/utils/utils';

export const MODULES = [HighchartsChartModule];

@Component({
  selector: 'app-smw-meteogram',
  templateUrl: './smw-meteogram.component.html',
  styleUrls: ['./smw-meteogram.component.scss'],
  imports: [...MODULES],
})
export class SmwMeteogramComponent {
  private injector = inject(Injector);
  public translate = inject(TranslateService);
  private themeService = inject(ThemeService);

  smwData = input<any>();
  station = input<StationInfo>();

  previousStation = signal<StationInfo>(null);
  Highcharts = Highcharts;
  chartConstructor = 'chart';
  chartOptions = {};
  chartCallback = null;
  highChartsData = [];
  updateFlag = false;

  lineChartParameters = [
    'tt',
    'td',
    't5',
    'tg0',
    'tg5',
    'tg10',
    'tg20',
    'tg30',
    'tg50',
    'tg100',
    'road_tt',
    'road_tt2',
    'road_tt3',
  ];
  barChartParameters = ['rrr', 'rr', 'snow_height'];

  constructor() {
    this.listenToStationChange();
    this.listenToThemeChanges();
  }

  private isDarkMode(): boolean {
    return this.themeService.theme() === 'dark';
  }

  private setChartData(): void {
    this.highChartsData = [];
    this.setHighChartsLineData();
    this.setHighChartsBarData();
  }

  setHighChartsBarData(): void {
    const startDtg = moment().subtract(24, 'hours');
    for (const parameter of this.barChartParameters) {
      const dataForParameter = [];
      let sum = 0;
      let barCount = 0;
      for (const dataset of this.smwData()) {
        if (
          dataset.hasOwnProperty(parameter) &&
          dataset[parameter] !== undefined
        ) {
          if (moment(dataset['dtg']).isAfter(startDtg)) {
            dataForParameter.push({
              x: Date.parse(dataset['dtg']),
              y: dataset[parameter],
            });
            sum += dataset[parameter];
            barCount += dataset[parameter] > 0 ? 1 : 0;
          }
        }
      }
      const showBar = sum > 0;
      if (dataForParameter.length > 0) {
        const highChartsPlot = {
          name: this.translate.instant(
            'PRODUCT.SMW.TABLE_PARAMETERS.' + parameter
          ),
          type: 'column',
          yAxis: 1,
          data: dataForParameter.sort((a, b) => {
            return a.x - b.x;
          }),
          pointPadding: 0.01,
          maxPointWidth: barCount > 50 ? 5 : 30,
          opacity: 0.7,
          tooltip: {
            // valueSuffix: parameter.indexOf('relRRS') > -1 ? ' cm' : ' mm'
            valueSuffix: parameter.indexOf('snow_height') > -1 ? ' cm' : ' mm',
          },
          color: this.parameterColor(parameter),
          visible: showBar,
        };
        this.highChartsData.push(highChartsPlot);
      }
    }
  }

  setHighChartsLineData(): void {
    const startDtg = moment().subtract(24, 'hours');
    for (const parameter of this.lineChartParameters) {
      const dataForParameter = [];
      for (const dataset of this.smwData()) {
        if (
          dataset.hasOwnProperty(parameter) &&
          dataset[parameter] !== undefined
        ) {
          if (moment(dataset['dtg']).isAfter(startDtg)) {
            dataForParameter.push({
              x: Date.parse(dataset['dtg']),
              y: dataset[parameter],
            });
          }
        }
      }
      if (dataForParameter.length > 0) {
        const highChartsPlot = {
          name: this.translate.instant(
            'PRODUCT.SMW.TABLE_PARAMETERS.' + parameter
          ),
          type: 'spline',
          data: dataForParameter.sort((a, b) => {
            return a.x - b.x;
          }),
          tooltip: {
            valueSuffix: ' °C',
          },
          color: this.parameterColor(parameter),
          marker: {
            enabled: false,
          },
        };
        this.highChartsData.push(highChartsPlot);
      }
    }
  }

  parameterColor(parameter: string): string {
    switch (parameter) {
      case 'tt':
        return '#F23D3D';
      case 'td':
        return '#399DE6';
      case 'road_tt':
        return '#001540';
      case 'road_tt2':
        return '#bcc2c2';
      case 'road_tt3':
        return '#798586';
      case 'tg100':
        return '#f2e5d9';
      case 'tg50':
        return '#ebd8c6';
      case 'tg30':
        return '#e5cbb3';
      case 'tg20':
        return '#debea0';
      case 'tg10':
        return '#ab713b';
      case 'tg5':
        return '#986434';
      case 'tg0':
        return '#724b27';
      case 't5':
        return '#4d3319';
      case 'rrr':
      case 'rr':
        return '#93C9F1';
      case 'ss1':
        return '#F79595';
    }
    return 'green';
  }

  setHighChartOptions(): void {
    const station = this.station();

    if (!station) {
      return;
    }

    const stationName = station.name;
    const isDark = this.isDarkMode();

    const textColor = isDark ? '#e5e7eb' : '#333333';
    const mutedTextColor = isDark ? 'rgba(229, 231, 235, 0.68)' : '#666666';
    const gridLineColor = isDark
      ? 'rgba(255, 255, 255, 0.14)'
      : 'rgba(0, 0, 0, 0.1)';
    const axisLineColor = isDark
      ? 'rgba(255, 255, 255, 0.28)'
      : 'rgba(0, 0, 0, 0.25)';
    const plotLineColor = isDark ? '#f87171' : '#9e9e9e';
    const secondaryAxisColor = isDark ? '#93c5fd' : '#357be6';

    Highcharts.setOptions({
      lang: {
        shortWeekdays: this.translate.instant('CHART.shortWeekdays'),
        printChart: this.translate.instant('CHART.printChart'),
        downloadJPEG: this.translate.instant('CHART.downloadJPEG'),
        downloadPNG: this.translate.instant('CHART.downloadPNG'),
        downloadPDF: this.translate.instant('CHART.downloadPDF'),
        downloadSVG: this.translate.instant('CHART.downloadSVG'),
        viewFullscreen: this.translate.instant('CHART.viewFullscreen'),
      },
      time: {
        useUTC: false,
      },
      chart: {
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'Roboto, sans-serif',
        },
      },
    });

    this.chartOptions = {
      chart: {
        zoomType: 'xy',
        marginLeft: 75,
        marginRight: 90,
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'Roboto, sans-serif',
        },
        events: {
          beforePrint(): void {
            this.setTitle(
              {
                text: 'Einsatzwetter',
              },
              {
                text:
                  'Temperaturen und Niederschlag ' +
                  stationName +
                  ' (' +
                  moment().format('DD.MM.YYYY HH:mm') +
                  ')',
                align: 'left',
              }
            );
          },
          afterPrint(): void {
            this.setTitle(
              {
                text: null,
              },
              {
                text: null,
              }
            );
          },
        },
      },
      title: {
        text: station.name,
        align: 'left',
        style: {
          color: textColor,
          fontWeight: '600',
        },
      },
      xAxis: [
        {
          type: 'datetime',
          labels: {
            format: '{value:%a %H:%M}',
            style: {
              color: textColor,
              fontSize: '11px',
              fontWeight: isDark ? '500' : '400',
            },
          },
          lineColor: axisLineColor,
          tickColor: axisLineColor,
          crosshair: {
            color: axisLineColor,
            dashStyle: 'Dash',
          },
          gridLineColor: gridLineColor,
        },
      ],
      legend: {
        reversed: false,
        itemStyle: {
          color: textColor,
        },
        itemHoverStyle: {
          color: isDark ? '#ffffff' : '#000000',
        },
        itemHiddenStyle: {
          color: mutedTextColor,
        },
      },
      yAxis: [
        {
          gridLineColor: gridLineColor,
          labels: {
            format: '{value}°C',
            style: {
              color: textColor,
              fontSize: '11px',
              fontWeight: isDark ? '500' : '400',
            },
          },
          title: {
            text: this.translate.instant('METPARA.' + 'ChartTT', {
              unit: '°C',
            }),
            style: {
              color: textColor,
              fontWeight: '600',
            },
          },
          softMin: 0,
          plotLines: [
            {
              value: 0,
              color: plotLineColor,
              width: isDark ? 2 : 1,
              zIndex: 2,
            },
          ],
          startOnTick: false,
        },
        {
          gridLineWidth: 0,
          title: {
            text: this.translate.instant('METPARA.' + 'ChartRR', {
              unit: 'mm',
            }),
            style: {
              color: secondaryAxisColor,
              fontWeight: '600',
            },
          },
          labels: {
            format: '{value} mm',
            style: {
              color: secondaryAxisColor,
              fontSize: '11px',
              fontWeight: isDark ? '500' : '400',
            },
          },
          startOnTick: false,
          opposite: true,
          softMax: 6,
          min: 0,
        },
      ],
      credits: {
        text: 'Wettermanufaktur GmbH',
        href: 'http://www.wettermanufaktur.de',
        style: {
          color: mutedTextColor,
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        headerFormat:
          '<small style="color:' +
          mutedTextColor +
          '">{point.x:%a, %d.%m. %H:%M}</small><br>',
        backgroundColor: isDark
          ? 'rgba(15, 23, 42, 0.95)'
          : 'rgba(255, 255, 255, 0.95)',
        borderColor: gridLineColor,
        borderRadius: 8,
        shadow: true,
        style: {
          color: textColor,
        },
      },
      plotOptions: {
        series: {
          turboThreshold: 10000,
        },
      },
      series: [...this.highChartsData],
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: ['viewFullscreen'],
            enabled: true,
            theme: {
              fill: 'transparent',
              states: {
                hover: {
                  fill: gridLineColor,
                },
              },
            },
          },
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              chart: {
                marginLeft: 40,
                marginRight: 55,
              },
              legend: {
                enabled: false,
                floating: false,
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
                x: 0,
                y: 0,
              },
              yAxis: [
                {
                  labels: {
                    format: '{value}°C',
                    style: {
                      color: textColor,
                    },
                  },
                  title: {
                    enabled: false,
                    text: this.translate.instant('METPARA.' + 'ChartTT'),
                    style: {
                      color: textColor,
                    },
                  },
                  softMin: 0,
                  plotLines: [
                    {
                      value: 0,
                      color: plotLineColor,
                      width: isDark ? 2 : 1,
                      zIndex: 2,
                    },
                  ],
                  startOnTick: false,
                },
                {
                  gridLineWidth: 0,
                  title: {
                    enabled: false,
                    text: this.translate.instant('METPARA.' + 'ChartRR'),
                    style: {
                      color: secondaryAxisColor,
                    },
                  },
                  labels: {
                    format: '{value} mm',
                    style: {
                      color: secondaryAxisColor,
                    },
                  },
                  startOnTick: false,
                  opposite: true,
                  softMax: 6,
                  min: 0,
                },
              ],
            },
          },
        ],
      },
    };
  }

  updateChart(): void {
    this.updateFlag = true;
  }

  private listenToThemeChanges(): void {
    effect(
      () => {
        this.themeService.theme();

        untracked(() => {
          if (!this.smwData() || !this.station()) {
            return;
          }

          this.setChartData();
          this.setHighChartOptions();
          this.updateChart();
        });
      },
      { injector: this.injector }
    );
  }

  private listenToStationChange() {
    effect(
      () => {
        const station = this.station();
        this.smwData();

        untracked(() => {
          if (!isNil(station) && this.previousStation() != station) {
            this.setChartData();
            this.setHighChartOptions();
            this.updateChart();
          }

          this.previousStation.set(station);
        });
      },
      { injector: this.injector }
    );
  }
}
